import React, { useState, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  Chip,
  Alert,
  Collapse,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SetMealIcon from "@mui/icons-material/SetMeal";
import SoupKitchenIcon from "@mui/icons-material/SoupKitchen";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CoffeeIcon from "@mui/icons-material/LocalCafe";
import BlendIcon from "@mui/icons-material/LocalBar";
import { format, addDays, startOfDay, endOfDay } from "date-fns";
import kitchenApi from "../../api/kitchen";
import useAuth from "../../hooks/useAuth";
import Layout from "../../components/layout/Layout";
import InstantRequirements from "./InstantRequirements";

// ─── helpers ─────────────────────────────────────────────────────────────────

const tomorrow = () => addDays(startOfDay(new Date()), 1);
const toInputDate = (d: Date) => format(d, "yyyy-MM-dd");
const fromInputDate = (s: string): Date => {
  const [y, m, day] = s.split("-").map(Number);
  return new Date(y, m - 1, day);
};
const displayDate = (d: Date) => format(d, "d MMM yyyy");

// ─── types ───────────────────────────────────────────────────────────────────

interface MealCount {
  mealId: string;
  mealName: string;
  count: number;
  hasKitchenIngredients: boolean;
}

interface DrinkCount {
  drinkId: string;
  drinkName: string;
  type: string;
  count: number;
  hasKitchenIngredients: boolean;
}

interface Ingredient {
  ingredientId: string;
  displayName: string;
  category: string;
  unit: string;
  totalQty: number;
  usedInMeals: string[];
}

interface RequirementsData {
  scheduledMenus?: {
    date: string;
    mainMeal: { id: string; name: string } | null;
    swapSetSerial: number;
    swapSet: { id: string; active: boolean; meals?: { id: string | null; name: string; active: boolean }[] } | null;
  }[];
  scheduleError?: string | null;
  mealCounts: MealCount[];
  drinkCounts?: DrinkCount[];
  missingIngredientMeals: string[];
  ingredients: Ingredient[];
  addOnCounts: Record<string, number>;
}

// ─── IngredientRow ────────────────────────────────────────────────────────────

const IngredientRow: React.FC<{ ing: Ingredient; last: boolean }> = ({
  ing,
  last,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: "flex",
          alignItems: "center",
          px: 3,
          py: 1.5,
          cursor: "pointer",
          borderBottom: last && !open ? "none" : "1px solid #f1f5f9",
          "&:hover": { backgroundColor: "#fafafa" },
          transition: "background 0.1s",
        }}
      >
        {/* Name — left */}
        <Typography
          sx={{ flex: 1, fontSize: "1rem", color: "#1e293b", fontWeight: 500 }}
        >
          {ing.displayName}
        </Typography>

        {/* Quantity — centre */}
        <Typography
          sx={{
            flex: 1,
            fontSize: "1rem",
            fontWeight: 700,
            color: "#f97316",
            textAlign: "center",
          }}
        >
          {ing.totalQty} {ing.unit}
        </Typography>

        {/* Meals + arrow — right */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={0.5}
          sx={{ flex: 1.4 }}
        >
          <Typography
            sx={{ fontSize: "1rem", color: "#64748b", fontWeight: 500 }}
          >
            {ing.usedInMeals.length} meal
            {ing.usedInMeals.length !== 1 ? "s" : ""}
          </Typography>
          {open ? (
            <KeyboardArrowUpIcon sx={{ fontSize: 18, color: "#f97316" }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
          )}
        </Stack>
      </Box>

      {/* Expanded detail */}
      <Collapse in={open}>
        <Box
          sx={{
            px: 3,
            py: 1,
            backgroundColor: "#f8fafc",
            borderBottom: last ? "none" : "1px solid #f1f5f9",
          }}
        >
          <Typography sx={{ fontSize: "0.9rem", color: "#64748b" }}>
            <Box component="span" sx={{ fontWeight: 600, color: "#475569" }}>
              Used in:{" "}
            </Box>
            {ing.usedInMeals.join(", ")}
          </Typography>
        </Box>
      </Collapse>
    </>
  );
};

// ─── IngredientSection ────────────────────────────────────────────────────────

const IngredientSection: React.FC<{
  category: string;
  ingredients: Ingredient[];
  last: boolean;
}> = ({ category, ingredients, last }) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <Box sx={{ borderBottom: last ? "none" : "1px solid #f1f5f9" }}>
      {/* Category label */}
      <Box
        onClick={() => setCollapsed((c) => !c)}
        sx={{
          px: 3,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          backgroundColor: "#f8fafc",
          cursor: "pointer",
          borderBottom: "1px solid #f1f5f9",
          "&:hover": { backgroundColor: "#f1f5f9" },
        }}
      >
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 700,
            color: "#94a3b8",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            flex: 1,
          }}
        >
          {category}
        </Typography>
        <Typography
          sx={{ fontSize: "0.78rem", color: "#2a2c2eff", fontWeight: 600 }}
        >
          {ingredients.length}
        </Typography>
        {collapsed ? (
          <KeyboardArrowDownIcon sx={{ fontSize: 15, color: "#cbd5e1" }} />
        ) : (
          <KeyboardArrowUpIcon sx={{ fontSize: 15, color: "#cbd5e1" }} />
        )}
      </Box>

      {/* Rows */}
      <Collapse in={!collapsed}>
        {ingredients.map((ing, i) => (
          <IngredientRow
            key={ing.ingredientId}
            ing={ing}
            last={i === ingredients.length - 1}
          />
        ))}
      </Collapse>
    </Box>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

type DateMode = "single" | "range";

const KitchenRequirementsPage: React.FC = () => {
  const { selectedKitchenId } = useAuth();
  const [requirementsTab, setRequirementsTab] = useState<"subscription" | "instant">("subscription");
  const [dateMode, setDateMode] = useState<DateMode>("single");
  const [singleDate, setSingleDate] = useState<Date>(tomorrow());
  const [rangeStart, setRangeStart] = useState<Date>(tomorrow());
  const [rangeEnd, setRangeEnd] = useState<Date>(addDays(tomorrow(), 6));

  const [data, setData] = useState<RequirementsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedSpan, setLoadedSpan] = useState<{
    start: Date;
    end: Date;
  } | null>(null);

  const requestId = React.useRef(0);
  const invalidateRequests = useCallback(() => { ++requestId.current; }, []);
  const doFetch = useCallback(async () => {
    const request = ++requestId.current;
    const start =
      dateMode === "single" ? startOfDay(singleDate) : startOfDay(rangeStart);
    const end =
      dateMode === "single" ? endOfDay(singleDate) : endOfDay(rangeEnd);

    if (dateMode === "range" && end < start) {
      setError("End date must be on or after start date.");
      return;
    }

    setLoading(true);
    setError(null);
    setLoaded(false);

    try {
      const res = await kitchenApi.getDailyRequirements(
        start.toISOString(),
        end.toISOString(),
        selectedKitchenId || undefined,
      );
      if (request !== requestId.current) return;
      setData(res);
      setLoadedSpan({ start, end });
      setLoaded(true);
    } catch (err: any) {
      if (request !== requestId.current) return;
      setError(
        err?.response?.data?.message || "Failed to fetch kitchen requirements.",
      );
    } finally {
      if (request === requestId.current) setLoading(false);
    }
  }, [dateMode, singleDate, rangeStart, rangeEnd, selectedKitchenId]);

  React.useEffect(() => {
    setData(null);
    doFetch();
    return invalidateRequests;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKitchenId]);

  const categorized: Record<string, Ingredient[]> = {};
  if (data) {
    data.ingredients.forEach((ing) => {
      const cat = ing.category || "Uncategorised";
      if (!categorized[cat]) categorized[cat] = [];
      categorized[cat].push(ing);
    });
  }

  const totalMeals = data?.mealCounts.reduce((s, m) => s + m.count, 0) ?? 0;
  const categoryKeys = Object.keys(categorized);

  const spanLabel = loadedSpan
    ? dateMode === "single"
      ? displayDate(loadedSpan.start)
      : `${displayDate(loadedSpan.start)} – ${displayDate(loadedSpan.end)}`
    : null;

  const inputStyle: React.CSSProperties = {
    padding: "6px 10px",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#0f172a",
    outline: "none",
    backgroundColor: "#f8fafc",
  };

  return (
    <Layout>
      {/* ── Page header ── */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 3 }}
        spacing={1}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: "#0f172a" }}>
            Ingredients - Kitchen Requirements
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {requirementsTab === "subscription" ? "Accumulated ingredient totals for confirmed orders" : "Ingredient preparation totals for planned Instant stock"}
          </Typography>
        </Box>
      </Stack>

      <Tabs value={requirementsTab} onChange={(_, value) => setRequirementsTab(value)} aria-label="Ingredient requirements" sx={{ mb: 3, borderBottom: "1px solid", borderColor: "divider" }}>
        <Tab value="subscription" id="subscription-ingredients-tab" aria-controls="subscription-ingredients-panel" label="Subscription ingredients" sx={{ textTransform: "none", fontWeight: 600 }} />
        <Tab value="instant" id="instant-ingredients-tab" aria-controls="instant-ingredients-panel" label="Instant ingredients" sx={{ textTransform: "none", fontWeight: 600 }} />
      </Tabs>

      {/* ── Controls ── */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          boxShadow: "none",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="flex-end"
          flexWrap="wrap"
        >
          {/* Mode toggle */}
          <Box>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                display: "block",
                mb: 0.5,
              }}
            >
              Mode
            </Typography>
            <Stack
              direction="row"
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {(["single", "range"] as DateMode[]).map((mode) => (
                <Box
                  key={mode}
                  onClick={() => setDateMode(mode)}
                  sx={{
                    px: 2,
                    py: 0.75,
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.15s",
                    backgroundColor:
                      dateMode === mode ? "#f97316" : "transparent",
                    color: dateMode === mode ? "white" : "#64748b",
                    borderRight:
                      mode === "single" ? "1px solid #e2e8f0" : "none",
                    "&:hover": {
                      backgroundColor:
                        dateMode === mode ? "#ea580c" : "#f8fafc",
                    },
                  }}
                >
                  {mode === "single" ? "Single Date" : "Date Range"}
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Date inputs */}
          {dateMode === "single" ? (
            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  display: "block",
                  mb: 0.5,
                }}
              >
                Date
              </Typography>
              <input
                type="date"
                value={toInputDate(singleDate)}
                onChange={(e) => setSingleDate(fromInputDate(e.target.value))}
                style={inputStyle}
              />
            </Box>
          ) : (
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  From
                </Typography>
                <input
                  type="date"
                  value={toInputDate(rangeStart)}
                  onChange={(e) => setRangeStart(fromInputDate(e.target.value))}
                  style={inputStyle}
                />
              </Box>
              <Typography color="text.secondary" sx={{ pb: 0.5 }}>
                –
              </Typography>
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  To
                </Typography>
                <input
                  type="date"
                  value={toInputDate(rangeEnd)}
                  min={toInputDate(rangeStart)}
                  onChange={(e) => setRangeEnd(fromInputDate(e.target.value))}
                  style={inputStyle}
                />
              </Box>
            </Stack>
          )}

          {/* Load */}
          <Box
            component="button"
            onClick={doFetch}
            disabled={loading}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2.5,
              py: 0.9,
              background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
              color: "white",
              border: "none",
              borderRadius: 2,
              fontSize: "0.875rem",
              fontWeight: 700,
              cursor: "pointer",
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? (
              <CircularProgress size={14} sx={{ color: "white" }} />
            ) : (
              <SearchIcon sx={{ fontSize: 16 }} />
            )}
            Load
          </Box>

          <Tooltip title="Reset to tomorrow">
            <IconButton
              size="small"
              onClick={() => {
                const t = tomorrow();
                setSingleDate(t);
                setRangeStart(t);
                setRangeEnd(addDays(t, 6));
              }}
              sx={{ border: "1px solid #e2e8f0", borderRadius: 2, p: 0.9 }}
            >
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {loading && (
        <Stack alignItems="center" sx={{ py: 8 }}>
          <CircularProgress color="primary" size={32} />
          <Typography color="text.secondary" variant="body2" sx={{ mt: 1.5 }}>
            Loading requirements…
          </Typography>
        </Stack>
      )}

      {!loading && loaded && data && (
        <Stack spacing={2.5} role="tabpanel" id={`${requirementsTab}-ingredients-panel`} aria-labelledby={`${requirementsTab}-ingredients-tab`}>
          {requirementsTab === "instant" ? (
            loadedSpan && <InstantRequirements startDate={toInputDate(loadedSpan.start)} endDate={toInputDate(loadedSpan.end)} />
          ) : <>
          {data.scheduleError && <Alert severity="warning">{data.scheduleError}</Alert>}
          {Boolean(data.scheduledMenus?.length) && (
            <Box component="section" aria-label="Scheduled menu" sx={{ bgcolor: "#fff", borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb", px: { xs: 2, md: 3 }, py: 2.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <SetMealIcon sx={{ fontSize: 20, color: "#f97316" }} />
                <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 700, color: "#1f2937" }}>Scheduled menu</Typography>
              </Stack>
              {data.scheduledMenus?.map((menu, dayIndex) => (
                <Box key={menu.date} sx={{ pt: dayIndex ? 2.5 : 0, mt: dayIndex ? 2.5 : 0, borderTop: dayIndex ? "1px solid #e5e7eb" : "none" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#667085", mb: 1.5 }}>{format(fromInputDate(menu.date), "EEE, d MMM yyyy")}</Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(220px, 0.7fr) minmax(0, 2fr)" }, gap: { xs: 2, md: 3 }, alignItems: "start" }}>
                    <Box sx={{ borderLeft: "3px solid #f97316", pl: 1.5, minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: "#667085", fontWeight: 600 }}>Main meal</Typography>
                      <Typography sx={{ mt: 0.5, fontSize: "1rem", fontWeight: 700, lineHeight: 1.5, color: "#1f2937", overflowWrap: "anywhere" }}>{menu.mainMeal?.name || "Not scheduled"}</Typography>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" sx={{ mb: 1.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#1f2937" }}>Swap-set {menu.swapSetSerial}{!menu.swapSet ? " (not configured)" : !menu.swapSet.active ? " (inactive)" : ""}</Typography>
                        {menu.swapSet?.meals && <Typography variant="caption" sx={{ color: "#667085" }}>{menu.swapSet.meals.length} {menu.swapSet.meals.length === 1 ? "meal" : "meals"}</Typography>}
                      </Stack>
                      {menu.swapSet?.meals && (menu.swapSet.meals.length ? (
                        <Box component="ul" aria-label={`Meals in swap-set ${menu.swapSetSerial} on ${menu.date}`} sx={{ display: "flex", flexWrap: "wrap", gap: 1, m: 0, p: 0, listStyle: "none" }}>
                          {menu.swapSet.meals.map((meal, index) => (
                            <Typography component="li" variant="body2" key={`${meal.id || "missing"}-${index}`} sx={{ maxWidth: "100%", px: 1.25, py: 0.75, border: "1px solid #e5e7eb", borderRadius: "6px", bgcolor: meal.id && meal.active ? "#f8fafc" : "#fff", overflowWrap: "anywhere", color: meal.id && meal.active ? "#344054" : "#667085", lineHeight: 1.5 }}>
                              {meal.name}{meal.id && !meal.active ? " (inactive)" : ""}
                            </Typography>
                          ))}
                        </Box>
                      ) : <Typography variant="body2" color="text.secondary">No meals in this swap-set</Typography>)}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {/* Span + meals label */}
          {spanLabel && (
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {spanLabel}
              {data.mealCounts.length > 0 && (
                <Box
                  component="span"
                  sx={{ ml: 1, color: "#f97316", fontWeight: 700 }}
                >
                  · {totalMeals} meal{totalMeals !== 1 ? "s" : ""}
                </Box>
              )}
            </Typography>
          )}

          {data.mealCounts.length === 0 ? (
            <Paper
              sx={{
                p: 6,
                textAlign: "center",
                borderRadius: 3,
                border: "1px solid #e2e8f0",
                boxShadow: "none",
              }}
            >
              <Typography color="text.secondary">
                No confirmed orders for this period.
              </Typography>
            </Paper>
          ) : (
            <>
              {/* Missing data warning */}
              {data.missingIngredientMeals.length > 0 && (
                <Alert
                  severity="warning"
                  sx={{
                    borderRadius: 2,
                    "& .MuiAlert-message": { fontSize: "0.8rem" },
                  }}
                >
                  <strong>Missing recipe data:</strong>{" "}
                  {data.missingIngredientMeals.join(", ")}
                </Alert>
              )}

              {/* Meals chips */}
              <Paper
                sx={{
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  boxShadow: "none",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f1f5f9" }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <SetMealIcon sx={{ fontSize: 20, color: "#f97316" }} />
                    <Typography
                      sx={{
                        fontSize: "1.1rem",
                        fontWeight: 700,
                        color: "#1e293b",
                      }}
                    >
                      Meals Scheduled
                    </Typography>
                  </Stack>
                </Box>
                <Box sx={{ px: 3, py: 2.5 }}>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {data.mealCounts.map((mc) => (
                      <Chip
                        key={mc.mealId.toString()}
                        label={`${mc.mealName}  ${mc.count}`}
                        size="small"
                        icon={
                          mc.hasKitchenIngredients ? undefined : (
                            <WarningAmberIcon />
                          )
                        }
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.95rem",
                          borderRadius: 1.5,
                          backgroundColor: mc.hasKitchenIngredients
                            ? "#f1f5f9"
                            : "#fef2f2",
                          color: mc.hasKitchenIngredients
                            ? "#334155"
                            : "#b91c1c",
                          border: "1px solid",
                          borderColor: mc.hasKitchenIngredients
                            ? "#e2e8f0"
                            : "#fecaca",
                          height: 32,
                          "& .MuiChip-icon": { fontSize: 14, color: "#ef4444" },
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              </Paper>

              {/* Drinks Scheduled */}
              {Array.isArray(data.drinkCounts) && data.drinkCounts.length > 0 && (
                <Paper
                  sx={{
                    borderRadius: 3,
                    border: "1px solid #e2e8f0",
                    boxShadow: "none",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f1f5f9" }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CoffeeIcon sx={{ fontSize: 20, color: "#d97706" }} />
                      <Typography
                        sx={{
                          fontSize: "1.1rem",
                          fontWeight: 700,
                          color: "#1e293b",
                        }}
                      >
                        Drinks Scheduled ({data.drinkCounts.reduce((sum, d) => sum + d.count, 0)})
                      </Typography>
                    </Stack>
                  </Box>
                  <Box sx={{ px: 3, py: 2.5 }}>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {data.drinkCounts.map((dc) => (
                        <Chip
                          key={dc.drinkId.toString()}
                          label={`${dc.drinkName}  ${dc.count}`}
                          size="small"
                          icon={
                            dc.type === "coffee" ? (
                              <CoffeeIcon sx={{ fontSize: "14px !important", color: "#b45309" }} />
                            ) : (
                              <BlendIcon sx={{ fontSize: "14px !important", color: "#047857" }} />
                            )
                          }
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            borderRadius: 1.5,
                            backgroundColor:
                              dc.type === "coffee" ? "#fef3c7" : "#ecfdf5",
                            color:
                              dc.type === "coffee" ? "#92400e" : "#065f46",
                            border: "1px solid",
                            borderColor:
                              dc.type === "coffee" ? "#fde68a" : "#a7f3d0",
                            height: 32,
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Paper>
              )}

              {/* Ingredients */}
              {categoryKeys.length > 0 && (
                <Paper
                  sx={{
                    borderRadius: 3,
                    border: "1px solid #e2e8f0",
                    boxShadow: "none",
                    overflow: "hidden",
                    width: 1000,
                  }}
                >
                  {/* Section title */}
                  <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f1f5f9" }}>
                    <Stack direction="row" alignItems="center">
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ flex: 1 }}
                      >
                        <SoupKitchenIcon
                          sx={{ fontSize: 20, color: "#f97316" }}
                        />
                        <Typography
                          sx={{
                            fontSize: "1.1rem",
                            fontWeight: 700,
                            color: "#1e293b",
                          }}
                        >
                          Ingredients Required
                        </Typography>
                      </Stack>
                      <Typography
                        sx={{
                          flex: 1,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          color: "#262729ff",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          textAlign: "center",
                        }}
                      >
                        Quantity
                      </Typography>
                      <Typography
                        sx={{
                          flex: 1.4,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          color: "#262729ff",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          textAlign: "right",
                        }}
                      >
                        Used In
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Categories + rows */}
                  {categoryKeys.map((cat, ci) => (
                    <IngredientSection
                      key={cat}
                      category={cat}
                      ingredients={categorized[cat]}
                      last={ci === categoryKeys.length - 1}
                    />
                  ))}
                </Paper>
              )}

              {/* Add-Ons */}
              {Object.keys(data.addOnCounts).length > 0 && (
                <Paper
                  sx={{
                    borderRadius: 3,
                    border: "1px solid #e2e8f0",
                    boxShadow: "none",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f1f5f9" }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <AddCircleOutlineIcon
                        sx={{ fontSize: 20, color: "#f97316" }}
                      />
                      <Typography
                        sx={{
                          fontSize: "1.1rem",
                          fontWeight: 700,
                          color: "#1e293b",
                        }}
                      >
                        Add-Ons
                      </Typography>
                    </Stack>
                  </Box>
                  {Object.entries(data.addOnCounts)
                    .sort(([, a], [, b]) => b - a)
                    .map(([name, count], i, arr) => (
                      <Box
                        key={name}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          px: 3,
                          py: 1.5,
                          borderBottom:
                            i < arr.length - 1 ? "1px solid #f1f5f9" : "none",
                        }}
                      >
                        <Typography
                          sx={{
                            flex: 1,
                            fontSize: "1rem",
                            color: "#1e293b",
                            fontWeight: 500,
                          }}
                        >
                          {name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1rem",
                            fontWeight: 700,
                            color: "#f97316",
                          }}
                        >
                          × {count}
                        </Typography>
                      </Box>
                    ))}
                </Paper>
              )}
            </>
          )}
          </>}
        </Stack>
      )}
    </Layout>
  );
};

export default KitchenRequirementsPage;
